import { reaction, computed, action, observable } from 'mobx';
import { ErrorBoundaryStore } from 'elc-error-boundary';
import { RouterStore } from 'elc-router';

export const NAVIGATE = 'navigate';

import {
    DEFAULT_ROUTE,
    CART_ROUTE,
    ORDER_ROUTE,
    SHIPPING_ROUTE,
    PAYMENT_ROUTE,
    SIGNUP_ROUTE,
    ACCOUNT_LANDING_ROUTE,
    LOGIN_ROUTE,
    ORDER_HISTORY_ROUTE,
    ORDER_DETAILS_ROUTE,
    CHECKOUT_ROUTE,
    ACCOUNT_ROUTE
} from '../../routes';

import { events, emit, watchQuery, query, serviceQueries } from 'elc-service-bus';

export const ORDER_SUCCESS_STATUS = '200';

export type orderErrorCodeType = '402' | '409' | '';

export interface IPaymentDetails {
    provider: string;
    publicKey: string;
    accountId: string;
    sandboxMode: boolean;
}

export type envType = 'qa' | 'integration' | 'production';
export type storageKeyType = 'localStorage' | 'sessionStorage';

export interface IPersistentStorage {
    getItem(key: string): string | null | undefined;

    setItem(key: string, value: string, storageKey?: storageKeyType): void;

    removeItem(key: string): void;

    storagePolling(key: string, callBack: Function): number | NodeJS.Timer;
}

export interface ICheckoutConfig {
    errorBoundaryStore: ErrorBoundaryStore;
    routerStore: RouterStore;
    persistentStorage: IPersistentStorage;
}

export interface IStringMap {
    [key: string]: string;
}

export class EcommStore {
    private registerWatchers: Function;
    public errorBoundaryStore: ErrorBoundaryStore;
    public routerStore: RouterStore;
    public persistentStorage: IPersistentStorage;
    @observable public isAddressFormValid: boolean = false;
    @observable public isEmaiOptInEnabled: boolean = false;
    @watchQuery()
    @observable
    public isCartPriceLimitExceeded: boolean;
    @watchQuery()
    @observable
    public isShippingScreenValid: boolean;

    @observable public offerCode: {};

    constructor(checkoutConfig: ICheckoutConfig) {
        this.registerWatchers();

        this.errorBoundaryStore = checkoutConfig.errorBoundaryStore;
        this.routerStore = checkoutConfig.routerStore;
        this.persistentStorage = checkoutConfig.persistentStorage;

        this.persistentStorage.storagePolling('CartHash', (cartHash: string) => {
            if (!cartHash && this.routerStore.currentRoute === ORDER_ROUTE) {
                this.routerStore.changeCurrentRoute(ORDER_ROUTE);

                return;
            }
            if (!cartHash) {
                window.location.reload();

                return;
            }
        });
        reaction(() => this.isAuthenticated, this.onAuthChanged);
    }

    @watchQuery()
    @observable
    public isAuthenticated: boolean = false;

    public goToSignUp = (payload?: { urlRedirectOnSuccess?: string }) => {
        // merge query params and payload data and extract urlRedirectOnSuccess param
        const { urlRedirectOnSuccess } = { ...this.routerStore.queryParams, ...payload } as {
            urlRedirectOnSuccess: string;
        };
        const queryParams: IStringMap = urlRedirectOnSuccess ? { urlRedirectOnSuccess } : {};

        this.routerStore.changeCurrentRoute(SIGNUP_ROUTE, {}, queryParams);
    };

    public onAuthChanged = async () => {
        const { urlRedirectOnSuccess } = this.routerStore.queryParams;
        if (urlRedirectOnSuccess) {
            return;
        }

        if (!this.isAuthenticated && this.routerStore.currentRoute === ACCOUNT_LANDING_ROUTE) {
            this.routerStore.changeCurrentRoute(LOGIN_ROUTE);
        }
        if (this.isAuthenticated && this.routerStore.currentRoute === LOGIN_ROUTE) {
            const isEmailJustVerified = await query(serviceQueries.IS_EMAIL_JUST_VERIFIED);
            if (isEmailJustVerified) {
                this.routerStore.changeCurrentRoute(ACCOUNT_ROUTE);
            } else {
                this.routerStore.changeCurrentRoute(ACCOUNT_LANDING_ROUTE);
            }
        }
    };

    public redirectOnSuccess = () => {
        const { urlRedirectOnSuccess } = this.routerStore.queryParams;

        if (urlRedirectOnSuccess) {
            const enforceRedirectOrigin = true;
            this.routerStore.changeLocation(urlRedirectOnSuccess, {}, enforceRedirectOrigin);
        } else {
            this.goToAccountLanding();
        }
    };
    public goToSignIn = (payload?: { urlRedirectOnSuccess?: string }) => {
        // merge query params and payload data and extract urlRedirectOnSuccess param
        const { urlRedirectOnSuccess } = { ...this.routerStore.queryParams, ...payload } as {
            urlRedirectOnSuccess?: string;
        };
        const queryParams: IStringMap = urlRedirectOnSuccess ? { urlRedirectOnSuccess } : {};

        this.routerStore.changeCurrentRoute(LOGIN_ROUTE, {}, queryParams);
    };

    public goToOrderHistory = () => {
        this.routerStore.changeCurrentRoute(ORDER_HISTORY_ROUTE);
    };

    public goToOrderDetails = (id: string) => {
        this.routerStore.changeCurrentRoute(ORDER_DETAILS_ROUTE, { id });
    };

    public goToCheckout = () => {
        this.routerStore.changeCurrentRoute(CHECKOUT_ROUTE);
    };

    public goToExternalUrl = (paymentUrl: string) => {
        this.routerStore.changeLocation(String(paymentUrl));
    };

    public goToCart = () => {
        this.routerStore.changeCurrentRoute(CART_ROUTE);
    };

    public goToAccountSettings() {
        this.routerStore.changeCurrentRoute(ACCOUNT_ROUTE);
    }

    public goToAccountLanding() {
        this.routerStore.changeCurrentRoute(ACCOUNT_LANDING_ROUTE);
    }

    public goToShipping = () => {
        this.routerStore.changeCurrentRoute(SHIPPING_ROUTE);
    };

    @action
    public shippingScreenSubmit = () => {
        if (this.isCartPriceLimitExceeded) {
            this.routerStore.changeCurrentRoute(CART_ROUTE);
        } else {
            emit(events.PRODUCT_OUT_OF_STOCK);
        }
        this.routerStore.changeCurrentRoute(PAYMENT_ROUTE);
    };

    public onEditShoppingBagClick = () => {
        this.routerStore.changeCurrentRoute(CART_ROUTE);
    };
    public onSessionExpired = () => {
        emit(events.SESSION_EXPIRED);
        this.routerStore.changeCurrentRoute(DEFAULT_ROUTE);
    };

    @action
    public reviewScreenSubmit = () => {
        this.routerStore.changeCurrentRoute(SHIPPING_ROUTE);
    };

    public onPageLoading = () => {
        if (window.location.hash === '#/payment/200') {
            return;
        }
    };

    @computed
    get orderTestFlag() {
        const orderTestFlag = this.persistentStorage.getItem('orderTestFlag');

        return orderTestFlag === 'true' || orderTestFlag === '1';
    }

    @action
    public handleSubmitError = (error: {}) => {
        console.error(error);
        throw error;
    };

    public destroy() {
        this.routerStore.destroy();
    }
}
